import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { fileUrl } from "../lib/api";

const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [queue, setQueueState] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const autoplayAttemptedRef = useRef(false);

  // Lazy-create the single global audio element.
  useEffect(() => {
    const a = new Audio();
    a.preload = "metadata";
    audioRef.current = a;
    const onTime = () => setCurrentTime(a.currentTime);
    const onMeta = () => setDuration(a.duration || 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      // Continuous loop — advance to next, wrap to 0.
      setCurrentIndex((idx) => {
        const next = queueRef.current.length ? (idx + 1) % queueRef.current.length : -1;
        return next;
      });
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnded);
    return () => {
      a.pause();
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnded);
    };
  }, []);

  // Keep queue accessible inside the stable 'ended' handler.
  const queueRef = useRef([]);
  useEffect(() => { queueRef.current = queue; }, [queue]);

  // Whenever currentIndex changes, load + try to play that track.
  const skipCountRef = useRef(0);
  const isMutedRef = useRef(isMuted);
  useEffect(() => { isMutedRef.current = isMuted; }, [isMuted]);

  const currentTrackPath = queue[currentIndex]?.audio_path || null;
  useEffect(() => {
    const a = audioRef.current;
    if (!a || currentIndex < 0) return;
    const trackPath = currentTrackPath;
    if (!trackPath) {
      // Avoid infinite skip if no slot has audio.
      const queueLen = queueRef.current.length;
      if (skipCountRef.current >= queueLen) {
        skipCountRef.current = 0;
        return;
      }
      skipCountRef.current += 1;
      const t = setTimeout(() => {
        setCurrentIndex((idx) => {
          if (!queueRef.current.length) return -1;
          return (idx + 1) % queueRef.current.length;
        });
      }, 200);
      return () => clearTimeout(t);
    }
    skipCountRef.current = 0;
    a.src = fileUrl(trackPath);
    a.currentTime = 0;
    a.muted = isMutedRef.current;
    const tryPlay = async () => {
      try {
        await a.play();
      } catch (err) {
        // Browser blocked unmuted autoplay — retry muted.
        a.muted = true;
        setIsMuted(true);
        try {
          await a.play();
        } catch (mutedErr) {
          if (process.env.NODE_ENV !== "production") {
            console.warn("Autoplay blocked even when muted; awaiting user gesture.", mutedErr?.message);
          }
        }
      }
    };
    tryPlay();
  }, [currentIndex, currentTrackPath]);

  const setQueue = useCallback((slots, { autoplayFirst = false } = {}) => {
    setQueueState(slots || []);
    queueRef.current = slots || [];
    if (autoplayFirst && !autoplayAttemptedRef.current && slots && slots.length > 0) {
      autoplayAttemptedRef.current = true;
      // Pick first slot that has audio, else 0.
      const firstWithAudio = slots.findIndex((s) => s.audio_path);
      setCurrentIndex(firstWithAudio >= 0 ? firstWithAudio : 0);
    }
  }, []);

  const play = useCallback((index) => {
    if (index == null) {
      // Resume current track if paused.
      const a = audioRef.current;
      if (a && a.src) a.play().catch(() => {});
      return;
    }
    setCurrentIndex(index);
  }, []);

  const pause = useCallback(() => {
    const a = audioRef.current;
    if (a) a.pause();
  }, []);

  const next = useCallback(() => {
    setCurrentIndex((idx) => {
      if (!queueRef.current.length) return -1;
      return (idx + 1) % queueRef.current.length;
    });
  }, []);

  const prev = useCallback(() => {
    setCurrentIndex((idx) => {
      if (!queueRef.current.length) return -1;
      return (idx - 1 + queueRef.current.length) % queueRef.current.length;
    });
  }, []);

  const seek = useCallback((time) => {
    const a = audioRef.current;
    if (a && isFinite(time)) {
      a.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  const toggleMute = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    a.muted = !a.muted;
    setIsMuted(a.muted);
  }, []);

  const value = useMemo(() => ({
    queue, currentIndex, isPlaying, isMuted, currentTime, duration,
    setQueue, play, pause, next, prev, seek, toggleMute,
    currentTrack: currentIndex >= 0 ? queue[currentIndex] : null,
  }), [queue, currentIndex, isPlaying, isMuted, currentTime, duration,
       setQueue, play, pause, next, prev, seek, toggleMute]);

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

export const usePlayer = () => useContext(PlayerContext);
